import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Clock, LayoutGrid, MessageSquareText, CalendarCheck, DollarSign, RotateCcw, Eye, Sparkles, Send } from 'lucide-react';
import { toast } from 'sonner';

// Static, illustrative recommendations — a real recommendation engine is
// not implemented in this environment yet; these demonstrate the intended
// experience described in the brief.
const RECOMMENDATIONS = [
  {
    key: 'posting_times', icon: Clock, priority: 'High',
    recommendation: 'Shift more posts to Tuesday & Thursday evenings',
    reason: 'Evenings tend to show stronger engagement windows for your content type (demo insight).',
    scope: 'All platforms', action: 'Update Preferred Posting Times',
    applyPatch: { cardType: 'postingTimes', patch: { days: ['Tue', 'Thu'], time_window: '7:00 PM – 9:00 PM' } },
  },
  {
    key: 'format_mix', icon: LayoutGrid, priority: 'Medium',
    recommendation: 'Produce more short-form clips from your best long-form videos',
    reason: 'Short-form content format typically reaches new audiences faster (demo insight).',
    scope: 'YouTube → Clips', action: 'Open Clips Review',
  },
  {
    key: 'message_refine', icon: MessageSquareText, priority: 'Medium',
    recommendation: 'Refine your message toward "practical, how-to" framing',
    reason: 'This framing tends to resonate with an engaged, growth-focused audience segment (demo insight).',
    scope: 'Creator Message', action: 'Update Creator Message',
    applyPatch: { cardType: 'message', patch: { target_outcome: 'Give the audience one practical, usable takeaway per post' } },
  },
  {
    key: 'consistency', icon: CalendarCheck, priority: 'High',
    recommendation: 'Improve publishing consistency',
    reason: 'Regular publishing cadence tends to compound audience growth over time (demo insight).',
    scope: 'All platforms', action: 'Review Publishing Schedule',
  },
  {
    key: 'monetization', icon: DollarSign, priority: 'Low',
    recommendation: 'Review monetization requirements for your most-connected platform',
    reason: 'Requirements shown are illustrative — worth checking against the platform\'s current rules directly.',
    scope: 'Platform Intelligence', action: 'Open Platform Intelligence',
  },
  {
    key: 'strategy', icon: RotateCcw, priority: 'Low',
    recommendation: 'Revisit your content strategy',
    reason: 'A strategy refresh can help if growth has slowed recently (demo insight — not based on real growth data yet).',
    scope: 'Creator Intelligence', action: 'Review Creator Intelligence',
  },
];

const PRIORITY_STYLE = {
  High: 'border-red-500/30 text-red-400',
  Medium: 'border-yellow-500/30 text-yellow-400',
  Low: 'border-white/10 text-[#8A93A6]',
};

/** Growth Recommendations detail dialog — turns the dashboard's other
 * sections into a short list of actionable, clearly-illustrative
 * suggestions. "Send to Project Manager" is a Level 2 demonstration only;
 * it does not create a real cross-feature task. */
export default function GrowthRecommendationsDialog({ open, onOpenChange, onApplyToIntel }) {
  const handleApply = (rec) => {
    if (!rec.applyPatch) { toast.info('This insight doesn\'t map to a Creator Intelligence field yet.'); return; }
    onApplyToIntel(rec.applyPatch.cardType, rec.applyPatch.patch);
  };
  const handleSendToPM = (rec) => {
    toast.info(`Demo Mode: "${rec.recommendation}" would be sent to Project Manager as a task — that connection isn't live yet.`);
  };
  const handleReview = (rec) => {
    toast.message(rec.recommendation, { description: rec.reason });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#14171F] border-white/10 sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="growth-recommendations-dialog">
        <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><Sparkles className="h-4.5 w-4.5 text-[#3A7BFF]" />Growth Recommendations</DialogTitle></DialogHeader>
        <p className="text-xs text-[#8A93A6] -mt-3">Illustrative suggestions based on general content patterns — not yet derived from your real performance data.</p>

        <div className="space-y-3">
          {RECOMMENDATIONS.map(rec => (
            <Card key={rec.key} className="glass border-white/5" data-testid={`growth-rec-${rec.key}`}>
              <CardContent className="pt-4 pb-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <span className="flex items-center gap-2 text-sm font-medium text-white"><rec.icon className="h-4 w-4 text-[#3A7BFF] shrink-0" />{rec.recommendation}</span>
                  <Badge variant="outline" className={`text-[10px] shrink-0 ${PRIORITY_STYLE[rec.priority]}`}>{rec.priority}</Badge>
                </div>
                <p className="text-xs text-[#8A93A6]">{rec.reason}</p>
                <p className="text-[11px] text-[#555E6E]">Scope: {rec.scope} · Suggested action: {rec.action}</p>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  <Button size="sm" variant="outline" className="h-7 text-xs border-white/10" onClick={() => handleReview(rec)} data-testid={`growth-review-${rec.key}`}><Eye className="h-3 w-3 mr-1" />Review Insight</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs border-white/10" onClick={() => handleApply(rec)} data-testid={`growth-apply-${rec.key}`}><Sparkles className="h-3 w-3 mr-1" />Apply to Creator Intelligence</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs border-white/10" onClick={() => handleSendToPM(rec)} data-testid={`growth-send-pm-${rec.key}`}><Send className="h-3 w-3 mr-1" />Send to Project Manager</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
